import React, { useRef, useState, useEffect, useCallback } from 'react';
import classes from './Register.module.css';
import fl_logo from '../../assets/fl_logo.png';
import { PersonOutlined } from '@material-ui/icons';
import { LockOpenOutlined } from '@material-ui/icons';
import CustomTextField from '../../ui/CustomTextField';
import CustomPasswordField from '../../ui/CustomPasswordField';
import CustomButton from '../../ui/CustomButton';
import CustomSpinner from '../../ui/CustomSpinner';
import useV3Axios from '../../hooks/useV3Axios';
import { useHistory, useLocation } from 'react-router-dom';
import { REGISTER_PATH, LOGIN_PATH } from '../../utils/paths';

const PASSWORD_RULES = [
    { label: 'At least 8 characters', test: v => v.length >= 8 },
    { label: 'One uppercase letter', test: v => /[A-Z]/.test(v) },
    { label: 'One lowercase letter', test: v => /[a-z]/.test(v) },
    { label: 'One digit', test: v => /\d/.test(v) },
    { label: 'One special character', test: v => /[\W_]/.test(v) },
];

const RegisterComplete = () => {
    const history = useHistory();
    const location = useLocation();
    const email = location.state?.email;
    const emailVerifiedToken = location.state?.emailVerifiedToken;

    const usernameRef = useRef(null);
    const passwordRef = useRef(null);
    const confirmRef = useRef(null);
    const codeInputRef = useRef(null);

    const [password, setPassword] = useState('');
    const [accountCodes, setAccountCodes] = useState([]);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const { isLoading, error: axiosError, data, sendRequest } = useV3Axios();

    useEffect(() => {
        if (!email || !emailVerifiedToken) history.replace(REGISTER_PATH);
    }, [email, emailVerifiedToken, history]);

    useEffect(() => {
        if (data) setSuccess(true);
    }, [data]);

    useEffect(() => {
        if (axiosError) setError(axiosError);
    }, [axiosError]);

    const addCode = useCallback(() => {
        const code = codeInputRef.current.value.trim().toUpperCase();
        if (!code) return;
        if (accountCodes.includes(code)) {
            codeInputRef.current.value = '';
            return;
        }
        setAccountCodes(prev => [...prev, code]);
        codeInputRef.current.value = '';
    }, [accountCodes]);

    const removeCode = useCallback((code) => {
        setAccountCodes(prev => prev.filter(c => c !== code));
    }, []);

    const handleCodeKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addCode();
        }
    }, [addCode]);

    const handleSubmit = useCallback((event) => {
        event.preventDefault();
        setError(null);

        const pwd = passwordRef.current.value;
        const allMet = PASSWORD_RULES.every(r => r.test(pwd));
        if (!allMet) {
            setError('Password does not meet all requirements.');
            return;
        }
        if (pwd !== confirmRef.current.value) {
            setError('Passwords do not match.');
            return;
        }
        if (accountCodes.length === 0) {
            setError('Add at least one account code to join.');
            return;
        }

        sendRequest('/register/complete', {
            emailVerifiedToken,
            username: usernameRef.current.value.trim(),
            password: pwd,
            accountCodes,
        });
    }, [sendRequest, emailVerifiedToken, accountCodes]);

    if (!email || !emailVerifiedToken) return null;

    if (success) {
        return (
            <div className={classes.Login}>
                <div className={classes.Card}>
                    <img className={classes.Logo} src={fl_logo} alt="logo" />
                    <p className={classes.Title}>FetalLite Dashboard</p>
                    <div className={classes.Form}>
                        <h3 style={{ textAlign: 'left', color: '#333' }}>Registration submitted</h3>
                        <p className={classes.Success}>
                            Your account is pending approval from the account admin. You will be able to log in once approved.
                        </p>
                        <div className={classes.Container}>
                            <CustomButton type='button' size='large' onClick={() => history.push(LOGIN_PATH)}>
                                GO TO SIGN IN
                            </CustomButton>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.Login}>
            <div className={classes.Card}>
                <img className={classes.Logo} src={fl_logo} alt="logo" />
                <p className={classes.Title}>FetalLite Dashboard</p>
                <form className={classes.Form} onSubmit={handleSubmit}>
                    <h3 style={{ textAlign: 'left', margin: '0 0 16px 0', color: '#333' }}>Set up your account</h3>
                    <CustomTextField margin="10px auto" reference={usernameRef} required id='username' label='Username' type='text'>
                        <PersonOutlined />
                    </CustomTextField>
                    <div style={{ margin: '10px 0 0 0' }}>
                        <CustomPasswordField reference={passwordRef} id='password' label='Password' onChange={e => setPassword(e.target.value)}>
                            <LockOpenOutlined />
                        </CustomPasswordField>
                        <div className={classes.Hint}>

                            {PASSWORD_RULES.map(rule => (
                                <div
                                    key={rule.label}
                                    className={`${classes.HintItem} ${rule.test(password) ? classes.HintItemMet : classes.HintItemUnmet}`}
                                >
                                    <span>{rule.test(password) ? '✓' : '○'}</span>
                                    <span>{rule.label}</span>
                                </div>
                            ))}
                        </div>
                        <div style={{ margin: '10px 0 0 0' }}>
                            <CustomPasswordField reference={confirmRef} id='confirmPassword' label='Confirm password'>
                                <LockOpenOutlined />
                            </CustomPasswordField>
                        </div>
                    </div>
                    <div style={{ margin: '20px 0 0 0' }}>
                        <p style={{ textAlign: 'left', fontSize: '14px', color: '#333', margin: '0 0 6px 0', fontWeight: '500' }}>
                            Account codes
                        </p>
                        <div className={classes.ChipsRow}>
                            {accountCodes.map(code => (
                                <span key={code} className={classes.Chip}>
                                    {code}
                                    <button type='button' className={classes.ChipRemove} onClick={() => removeCode(code)}>×</button>
                                </span>
                            ))}
                        </div>
                        <div className={classes.AddRow}>
                            <input
                                ref={codeInputRef}
                                className={classes.AddInput}
                                type='text'
                                placeholder='e.g. SMC-A3X9K'
                                onKeyDown={handleCodeKeyDown}
                            />
                            <CustomButton type='button' size='small' onClick={addCode}>ADD</CustomButton>
                        </div>
                    </div>
                    {error && <p className={classes.Error}>{error}</p>}
                    <div className={classes.Container}>
                        {isLoading ? <CustomSpinner /> : <CustomButton type='submit' size='large'>REGISTER</CustomButton>}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RegisterComplete;
